import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Bot, User, ExternalLink } from 'lucide-react';
import { cn } from '@hts/ui';
import { useGet, apiPost } from '../../hooks/useApi.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { relativeTime } from '../../lib/format.js';
import type { AiMessage } from '../../api/types.js';

const SUGGESTIONS = [
  'Which technicians are best for P1 network issues?',
  'What\'s our SLA compliance this week?',
  'Show me sentiment trend for this month',
  'Which clients have the most escalations?',
  'Who should I assign VPN tickets to?',
];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// GET /api/v1/ai/status (apps/api/src/routes/ai-chat.ts): whether Ask can
// answer for this tenant (copilot enabled + Claude integration / env key).
interface AiStatus {
  enabled: boolean;
  provider: 'claude' | 'env' | null;
  reason: string | null;
}

export function AskAiPage() {
  const navigate = useNavigate();
  const { data, loading, error: historyError, refetch } = useGet<{ items: AiMessage[] }>('/api/v1/ai/chat');
  const { data: status, loading: statusLoading, refetch: refetchStatus } = useGet<AiStatus>('/api/v1/ai/status');
  const disabled = status ? !status.enabled : false;
  const [input, setInput] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState<string | undefined>(undefined);
  const [streamingId, setStreamingId] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState<string | null>(null);
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  const messages = data?.items ?? [];

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, sending]);

  async function send(text?: string) {
    const content = (text ?? input).trim();
    if (!content || sending || disabled) return;
    setInput('');
    setPending(content);
    setSending(true);
    setSendError(undefined);
    try {
      const resp = await apiPost<AiMessage>('/api/v1/ai/chat', { content });
      setStreamingId(resp.id);
      refetch();
      setTimeout(() => setStreamingId(null), 500);
    } catch (e) {
      // Surface the API's message (e.g. "copilot is disabled for this tenant")
      // instead of silently swallowing the failure.
      setSendError(String((e as Error)?.message ?? e));
      setInput(content); // keep the question so the user can retry
      refetchStatus();
    } finally {
      setPending(null);
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }

  return (
    <div className="flex flex-col h-full max-w-3xl mx-auto">
      {/* Header */}
      <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-md">
            <Bot className="h-4 w-4" />
          </div>
          <div>
            <h1 className="text-base font-bold text-[hsl(var(--content))]">Ask AI</h1>
            <p className="text-xs text-[hsl(var(--content-subtle))]">Ask anything about tickets, technicians, or performance</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5" title={status?.reason ?? undefined}>
            <span className={cn(
              'h-2 w-2 rounded-full',
              statusLoading && !status ? 'bg-gray-400' : status?.enabled ? 'bg-green-500 animate-pulse' : 'bg-red-500'
            )} />
            <span className="text-xs text-[hsl(var(--content-subtle))]">
              {statusLoading && !status ? 'Checking…' : status?.enabled ? 'Online' : status ? 'Not configured' : 'Unknown'}
            </span>
          </div>
        </div>
      </div>

      {disabled && status?.reason && (
        <div className="mx-6 mt-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200 flex items-center gap-3">
          <span className="flex-1">{status.reason}</span>
          <button
            onClick={() => navigate('/admin/integrations')}
            className="shrink-0 rounded-lg bg-amber-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-700"
          >
            Open Integrations
          </button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        {historyError && (
          <div className="flex justify-center">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
              Couldn't load chat history ({historyError}).
              <button onClick={refetch} className="underline font-medium">Retry</button>
            </span>
          </div>
        )}
        {/* Welcome */}
        {messages.length === 0 && !loading && !historyError && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-lg mb-5">
              <Bot className="h-8 w-8" />
            </div>
            <h2 className="text-lg font-bold text-[hsl(var(--content))] mb-2">TechSys AI Assistant</h2>
            <p className="text-sm text-[hsl(var(--content-subtle))] max-w-sm">
              I can help you find the best technician for a ticket, analyze call sentiment, review SLA performance, and more.
            </p>
          </div>
        )}

        {loading && (
          <div className="space-y-4">
            <CardSkeleton lines={2} />
            <CardSkeleton lines={3} />
          </div>
        )}

        {messages.map(msg => (
          <MessageBubble
            key={msg.id}
            message={msg}
            streaming={msg.id === streamingId}
            onOpenTicket={id => navigate(`/tickets/${id}`)}
          />
        ))}

        {pending && (
          <MessageBubble
            message={{ id: 'pending', role: 'user', content: pending, createdAt: new Date().toISOString() }}
            onOpenTicket={() => undefined}
          />
        )}
        {sending && <TypingIndicator />}
        {sendError && (
          <div className="flex justify-center py-2">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
              <Bot className="h-3.5 w-3.5 shrink-0" />
              {sendError}
            </span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Suggestions */}
      {messages.length <= 2 && !loading && !disabled && (
        <div className="px-6 py-3 border-t border-[hsl(var(--border))]">
          <p className="text-xs font-medium text-[hsl(var(--content-subtle))] mb-2">Try asking:</p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map(s => (
              <button
                key={s}
                onClick={() => send(s)}
                disabled={sending}
                className="px-3 py-1.5 rounded-full text-xs border border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:border-brand-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-6 py-4 border-t border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
        <div className="flex items-end gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] px-4 py-3 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20 transition-all">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={disabled ? 'Ask AI is not configured for this tenant' : 'Ask about tickets, technicians, sentiment…'}
            disabled={disabled}
            rows={1}
            className="flex-1 bg-transparent text-sm text-[hsl(var(--content))] placeholder:text-[hsl(var(--content-placeholder))] outline-none resize-none min-h-[24px] max-h-40 overflow-y-auto"
            style={{ lineHeight: '1.5' }}
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || sending || disabled}
            className="flex h-8 w-8 items-center justify-center rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shrink-0"
            aria-label="Send message"
          >
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="text-[11px] text-[hsl(var(--content-placeholder))] mt-2 text-center">
          ↵ send · Shift+↵ new line · AI responses use live ticket and tech data
        </p>
      </div>
    </div>
  );
}

function MessageBubble({ message, streaming, onOpenTicket }: { message: AiMessage; streaming?: boolean; onOpenTicket: (id: string) => void }) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex gap-3', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 text-white mt-1">
          <Bot className="h-3.5 w-3.5" />
        </div>
      )}

      <div className={cn('max-w-[80%] space-y-1.5')}>
        <div className={cn(
          'rounded-2xl px-4 py-3 text-sm leading-relaxed',
          isUser
            ? 'bg-brand-600 text-white rounded-br-sm'
            : 'bg-[hsl(var(--surface))] border border-[hsl(var(--border))] text-[hsl(var(--content))] rounded-bl-sm shadow-sm',
          streaming && 'animate-pulse'
        )}>
          <MessageContent content={message.content} isUser={isUser} />
        </div>

        {/* Citations */}
        {message.citations && message.citations.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-1">
            {message.citations.map((c, i) => {
              const cls = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300';
              // Ticket citations carry the tickets_cache uuid -> deep-link to the ticket page.
              return c.entity === 'ticket' && UUID_RE.test(c.id) ? (
                <button key={i} onClick={() => onOpenTicket(c.id)} className={cn(cls, 'hover:bg-blue-100 dark:hover:bg-blue-900/40')}>
                  <ExternalLink className="h-2.5 w-2.5" />
                  {c.label}
                </button>
              ) : (
                <span key={i} className={cls}>{c.label}</span>
              );
            })}
          </div>
        )}

        <p className="text-[11px] text-[hsl(var(--content-placeholder))] px-1">{relativeTime(message.createdAt)}</p>
      </div>

      {isUser && (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 mt-1">
          <User className="h-3.5 w-3.5" />
        </div>
      )}
    </div>
  );
}

function MessageContent({ content, isUser }: { content: string; isUser: boolean }) {
  // Simple markdown-ish rendering for bold and line breaks
  const parts = content.split('\n');
  return (
    <div className="space-y-1">
      {parts.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-2" />;
        const segments = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={i}>
            {segments.map((seg, j) => {
              if (seg.startsWith('**') && seg.endsWith('**')) {
                return <strong key={j} className={isUser ? 'text-white font-bold' : 'text-[hsl(var(--content))] font-semibold'}>{seg.slice(2, -2)}</strong>;
              }
              return seg;
            })}
          </p>
        );
      })}
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-3 justify-start">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 text-white mt-1">
        <Bot className="h-3.5 w-3.5" />
      </div>
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
        <div className="flex items-center gap-1">
          {[0, 150, 300].map(d => (
            <span
              key={d}
              className="h-2 w-2 rounded-full bg-[hsl(var(--content-subtle))] animate-bounce"
              style={{ animationDelay: `${d}ms` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
